/**
 * Detalle de una recompensa: info completa; el canje va anclado en la barra
 * inferior. Reutiliza GET /rewards (busca el item por id). Vuelve a /student/rewards
 * con el botón "atrás" (la barra de navegación se oculta en esta pantalla).
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CircleStar } from 'lucide-react';
import { Spinner } from '@/components/atoms/Spinner';
import { Button } from '@/components/atoms/Button';
import { ErrorState } from '@/components/molecules/ErrorState';
import { useToast } from '@/context/ToastContext';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { useXP } from '@/context/XPContext';
import {
  getRewards,
  redeemReward,
  type RewardItem,
  type RewardsResponse,
} from '@/services/api/rewards.api';
import { rewardTitle, rewardDesc, rewardIcon, rewardImage } from './rewardMeta';
import styles from './RewardsInvitePages.module.scss';

export function RewardDetailPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { refreshXp } = useXP();
  const { rewardId } = useParams<{ rewardId: string }>();
  const bottomBar = useStoryBottomBar();
  const setBottomBarContent = bottomBar?.setContent;
  const [data, setData] = useState<RewardsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      setData(await getRewards());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const item: RewardItem | undefined = data?.items.find((r) => r.id === rewardId);

  const handleRedeem = useCallback(async () => {
    if (!item) return;
    setRedeeming(true);
    try {
      await redeemReward(item.id);
      toast({ variant: 'success', description: t('rewardsPage.redeemed') });
      await Promise.all([load(), refreshXp()]);
    } catch {
      toast({ description: t('rewardsPage.redeemError') });
    } finally {
      setRedeeming(false);
    }
  }, [item, load, refreshXp, toast, t]);

  // Botón de canje anclado en la barra inferior global (se re-publica al cambiar
  // el estado para cerrar sobre los valores actuales).
  useEffect(() => {
    if (!setBottomBarContent) return;
    if (!item) {
      setBottomBarContent(null);
      return;
    }
    setBottomBarContent(
      item.comingSoon ? (
        <Button variant="primary" size="lg" fullWidth disabled>
          {t('rewardsPage.comingSoon')}
        </Button>
      ) : item.owned ? (
        <Button variant="primary" size="lg" fullWidth disabled>
          {t('rewardsPage.owned')}
        </Button>
      ) : (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!item.redeemable || redeeming}
          isLoading={redeeming}
          onClick={() => void handleRedeem()}
        >
          {item.redeemable ? t('rewardsPage.redeem') : t('rewardsPage.notEnough')}
        </Button>
      )
    );
  }, [setBottomBarContent, item, redeeming, handleRedeem, t]);

  useEffect(() => () => setBottomBarContent?.(null), [setBottomBarContent]);

  if (loading && !data) {
    return (
      <div className={styles.page__placeholder}>
        <Spinner size="lg" aria-label={t('common.loading')} />
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.page}>
        <ErrorState onRetry={() => void load()} />
      </div>
    );
  }
  if (!item) {
    return (
      <div className={styles.page__placeholder}>
        <p>{t('rewardsPage.notFound')}</p>
      </div>
    );
  }

  const Icon = rewardIcon(item);
  const image = rewardImage(item);

  return (
    <div className={styles.page}>
      <div className={styles.rewardDetail}>
        {image ? (
          <img src={image} alt="" className={styles.rewardDetail__hero} />
        ) : (
          <span className={styles.rewardDetail__icon}>
            <Icon size={38} aria-hidden />
          </span>
        )}
        <h1 className={styles.rewardDetail__title}>{rewardTitle(item, t, i18n.language)}</h1>
        <p className={styles.rewardDetail__desc}>{rewardDesc(item, t, i18n.language)}</p>

        <span className={styles.rewardDetail__cost}>
          <CircleStar size={18} aria-hidden /> {t('rewardsPage.cost', { cost: item.cost })}
        </span>

        {item.comingSoon ? (
          <p className={styles.rewardDetail__hint}>{t('rewardsPage.comingSoonHint')}</p>
        ) : (
          <>
            <div className={styles.rewardDetail__balance}>
              {t('rewardsPage.walletLabel')}: <strong>{data?.balance ?? 0}</strong>
            </div>

            <p className={styles.rewardDetail__hint}>{t('rewardsPage.walletHint')}</p>
          </>
        )}
      </div>
    </div>
  );
}

RewardDetailPage.displayName = 'RewardDetailPage';
export default RewardDetailPage;
